import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';

export function LoginForm() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle, user, syncForumSSO } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get returnTo parameter from URL
  const query = new URLSearchParams(location.search);
  const returnTo = query.get('returnTo');

  // If user is already logged in and we have a returnTo, redirect immediately
  useEffect(() => {
    if (user && returnTo) {
      console.log('User already logged in, redirecting to:', returnTo);
      // For forum redirects, use window.location if it's an external path or subpath
      if (returnTo.startsWith('/forum') || returnTo.startsWith('http')) {
        // Sync before redirecting - needs to be awaited or handled via callback
        if (syncForumSSO) {
          syncForumSSO()
            .catch(e => console.warn('SSO Sync error during auto-redirect:', e))
            .finally(() => {
              window.location.href = returnTo;
            });
        } else {
          window.location.href = returnTo;
        }
      } else {
        navigate(returnTo);
      }
    }
  }, [user, returnTo, navigate, syncForumSSO]);

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();

      // After successful login, trigger SSO sync for the forum
      if (syncForumSSO) {
        await syncForumSSO();
      }

      // After Google sign in, Navigate either to returnTo or /
      if (returnTo) {
        if (returnTo.startsWith('/forum') || returnTo.startsWith('http')) {
          window.location.href = returnTo;
        } else {
          navigate(returnTo);
        }
      } else {
        navigate('/');
      }
    } catch (err: any) {
      // Check for trial expiration error
      if (err.code === 'auth/trial-expired') {
        navigate('/trial-expired');
        return;
      }
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-4">
            <img src="/logo.jpg" alt="CanvasEstate Logo" className="h-16 w-auto rounded-lg shadow-md" loading="lazy" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-brand-secondary">
            CanvasEstate
          </h2>
          <p className="mt-2 text-center text-sm font-bold text-brand-primary uppercase tracking-wide">
            Let's Canvas Your Estate
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-slate-300 rounded-md shadow-sm bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-slate-50 text-slate-500">Or sign in with email</span>
          </div>
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          setLoading(true);
          setError('');
          const formData = new FormData(e.currentTarget);
          const email = formData.get('email') as string;
          const password = formData.get('password') as string;
          signIn(email, password)
            .then(async () => {
              // Sync SSO before redirecting
              if (syncForumSSO) {
                try {
                  await syncForumSSO();
                } catch (e) {
                  console.warn('Post-login SSO sync failed:', e);
                }
              }

              if (returnTo) {
                if (returnTo.startsWith('/forum') || returnTo.startsWith('http')) {
                  window.location.href = returnTo;
                } else {
                  navigate(returnTo);
                }
              } else {
                navigate('/');
              }
            })
            .catch(err => {
              // Check for trial expiration error
              if (err.code === 'auth/trial-expired') {
                navigate('/trial-expired');
                return;
              }
              // Redirect to register if user not found
              if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-login-credentials') {
                navigate(`/register?email=${encodeURIComponent(email)}`);
                return;
              }
              setError(err.message || 'Failed to sign in');
            })
            .finally(() => {
              setLoading(false);
            });
        }} className="space-y-4">
          <div>
            <label htmlFor="email" className="sr-only">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              disabled={loading}
              className="appearance-none relative block w-full px-3 py-2 border border-slate-300 placeholder-slate-500 text-slate-900 rounded-t-md focus:outline-none focus:ring-brand-primary focus:border-brand-primary focus:z-10 sm:text-sm disabled:opacity-50"
              placeholder="Email address"
            />
          </div>
          <div>
            <label htmlFor="password" className="sr-only">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              disabled={loading}
              className="appearance-none relative block w-full px-3 py-2 border border-slate-300 placeholder-slate-500 text-slate-900 rounded-b-md focus:outline-none focus:ring-brand-primary focus:border-brand-primary focus:z-10 sm:text-sm disabled:opacity-50"
              placeholder="Password"
            />
          </div>
          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-brand-primary hover:text-emerald-700 transition-colors"
            >
              Forgot your password?
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}